import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import {
    ChevronLeft,
    CheckCircle,
    XCircle,
    Clock,
    Smartphone,
    Calendar,
    AlertCircle,
    Sparkles,
    X
} from 'lucide-react';
import './AdminPages.css';
import './AdminWorkspaceDetail.css';
import '../TestResults.css'; // Leverage existing styles for device frames, panels, and lists

function AdminRunDetail() {
    const { id } = useParams();
    const [runData, setRunData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStepIndex, setSelectedStepIndex] = useState(null);
    const [activeTab, setActiveTab] = useState('screenshots'); // screenshots, logs, metadata
    const [zoomedImage, setZoomedImage] = useState(null);

    const loadRunDetail = useCallback(async () => {
        try {
            setLoading(true);
            const data = await adminManagementService.getRunDetail(id);
            setRunData(data);
            
            // Set initial selected step (first failed step or first step)
            if (data?.run?.executedSteps) {
                const steps = typeof data.run.executedSteps === 'string'
                    ? JSON.parse(data.run.executedSteps)
                    : data.run.executedSteps;
                if (Array.isArray(steps) && steps.length > 0) {
                    const firstFailed = steps.findIndex(s => !s.success);
                    setSelectedStepIndex(firstFailed >= 0 ? firstFailed : 0);
                }
            }
        } catch (err) {
            setError('Failed to load test execution details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadRunDetail();
    }, [loadRunDetail]);

    if (loading) return <div className="admin-loading">Loading test run details...</div>;
    if (error || !runData || !runData.run) {
        return (
            <div className="admin-page">
                <div className="admin-breadcrumb">
                    <Link to="/admin/runs">
                        <ChevronLeft size={16} />
                        Back to Test Runs
                    </Link>
                </div>
                <div className="admin-form-error">
                    <AlertCircle size={18} /> {error || 'Test run not found'}
                </div>
            </div>
        );
    }

    const { run, appName } = runData;
    const steps = run.executedSteps
        ? (typeof run.executedSteps === 'string'
            ? JSON.parse(run.executedSteps)
            : run.executedSteps)
        : [];
    const selectedStep = selectedStepIndex !== null ? steps[selectedStepIndex] : null;

    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        const seconds = Math.floor(ms / 1000);
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'success': return 'success';
            case 'failed': return 'danger';
            case 'error': return 'danger';
            case 'running': return 'warning';
            case 'pending': return 'warning';
            case 'queued': return 'warning';
            case 'cancelled': return 'warning';
            default: return 'warning';
        }
    };

    const getStatusIcon = (success) => {
        return success 
            ? <CheckCircle size={16} style={{ color: '#10b981', flexShrink: 0 }} />
            : <XCircle size={16} style={{ color: '#ef4444', flexShrink: 0 }} />;
    };

    // Metrics calculation
    const totalSteps = steps.length;
    const passCount = steps.filter(s => s.success).length;
    const passRate = totalSteps > 0 ? Math.round((passCount / totalSteps) * 100) : 0;

    return (
        <div className="admin-page test-results" style={{ padding: '0 24px' }}>
            <div className="admin-breadcrumb" style={{ marginBottom: '16px' }}>
                <Link to="/admin/runs">
                    <ChevronLeft size={16} />
                    Back to Test Runs
                </Link>
            </div>

            {/* Test Run Header */}
            <div className="test-header-info" style={{ borderBottom: '1px solid #334155', paddingBottom: '24px', marginBottom: '24px' }}>
                <div className="test-title-section">
                    <h1 style={{ fontSize: '1.6rem', color: '#f8fafc', marginBottom: '8px' }}>
                        Run #{run.id.substring(0, 8)} details
                    </h1>
                    <div style={{ margin: '12px 0', width: '100%', maxWidth: '800px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Test Goal</span>
                        <textarea
                            readOnly
                            value={run.goalText || run.testGoal?.goalText || 'No Goal Text'}
                            style={{
                                width: '100%',
                                minHeight: '60px',
                                maxHeight: '120px',
                                backgroundColor: '#0f172a',
                                border: '1px solid #334155',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                color: '#cbd5e1',
                                fontSize: '0.85rem',
                                resize: 'vertical',
                                outline: 'none',
                                fontFamily: 'inherit',
                                lineHeight: '1.4'
                            }}
                        />
                    </div>
                    <div className="test-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '0.85rem', color: '#94a3b8' }}>
                        <span className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Smartphone size={14} />
                            Platform: <strong style={{ color: '#cbd5e1', textTransform: 'uppercase' }}>{run.platform || 'N/A'}</strong>
                        </span>
                        <span className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Clock size={14} />
                            Duration: <strong style={{ color: '#cbd5e1' }}>{formatDuration(run.duration)}</strong>
                        </span>
                        <span className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Calendar size={14} />
                            Started: <strong style={{ color: '#cbd5e1' }}>{new Date(run.createdAt).toLocaleString()}</strong>
                        </span>
                        <span className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Workspace: <strong style={{ color: '#cbd5e1' }}>{run.workspace?.name || 'N/A'}</strong>
                        </span>
                        <span className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            App: <strong style={{ color: '#cbd5e1' }}>{appName}</strong>
                        </span>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {run.testGoalId && (
                        <Link 
                            to={`/admin/goals/${run.testGoalId}`}
                            className="admin-cancel-btn"
                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', fontSize: '0.9rem' }}
                        >
                            View Goal
                        </Link>
                    )}
                    <span className={`status-badge ${getStatusClass(run.status)}`} style={{ fontSize: '0.9rem', padding: '6px 16px' }}>
                        {run.status.toUpperCase()}
                    </span>
                </div>
            </div>

            <div className="results-content" style={{ display: 'grid', gridTemplateColumns: '320px 1fr 340px', gap: '24px', height: 'calc(100vh - 220px)', minHeight: '600px' }}>
                {/* Left Panel: Executed Steps */}
                <div className="steps-panel" style={{ display: 'flex', flexDirection: 'column', background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
                    <div className="panel-header" style={{ padding: '16px', borderBottom: '1px solid #334155', background: '#0f172a' }}>
                        <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Executed Steps ({totalSteps})
                        </h3>
                    </div>

                    <div className="steps-list" style={{ overflowY: 'auto', flex: 1, padding: '12px' }}>
                        {steps.map((step, index) => (
                            <div
                                key={index}
                                className={`step-item ${!step.success ? 'failed' : 'passed'} ${selectedStepIndex === index ? 'selected' : ''}`}
                                onClick={() => setSelectedStepIndex(index)}
                                style={{
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '8px',
                                    cursor: 'pointer',
                                    borderLeft: '4px solid',
                                    borderLeftColor: step.success ? '#10b981' : '#ef4444',
                                    backgroundColor: selectedStepIndex === index ? '#334155' : '#0f172a',
                                    transition: 'background-color 0.2s'
                                }}
                            >
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                    {getStatusIcon(step.success)}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '0.85rem', color: '#f8fafc', fontWeight: 600 }}>
                                            Step {index + 1}: {step.title || step.action}
                                        </div>
                                        {step.target && (
                                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                                                Target: <code style={{ backgroundColor: '#1e293b', padding: '1px 4px', borderRadius: '4px' }}>{typeof step.target === 'object' ? JSON.stringify(step.target) : step.target}</code>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {steps.length === 0 && (
                            <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', padding: '24px 0' }}>
                                No executed steps recorded.
                            </div>
                        )}
                    </div>
                </div>

                {/* Center Panel: Screenshots / Logs / Raw Metadata */}
                <div className="screenshot-panel" style={{ display: 'flex', flexDirection: 'column', background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
                    <div className="logs-tabs" style={{ display: 'flex', background: '#0f172a', borderBottom: '1px solid #334155' }}>
                        <button
                            className={`tab ${activeTab === 'screenshots' ? 'active' : ''}`}
                            onClick={() => setActiveTab('screenshots')}
                            style={{
                                flex: 1,
                                padding: '14px',
                                background: activeTab === 'screenshots' ? '#1e293b' : 'transparent',
                                border: 'none',
                                color: activeTab === 'screenshots' ? '#38bdf8' : '#94a3b8',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                borderBottom: activeTab === 'screenshots' ? '2px solid #38bdf8' : 'none'
                            }}
                        >
                            Screenshots
                        </button>
                        <button
                            className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
                            onClick={() => setActiveTab('logs')}
                            style={{
                                flex: 1,
                                padding: '14px',
                                background: activeTab === 'logs' ? '#1e293b' : 'transparent',
                                border: 'none',
                                color: activeTab === 'logs' ? '#38bdf8' : '#94a3b8',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                borderBottom: activeTab === 'logs' ? '2px solid #38bdf8' : 'none'
                            }}
                        >
                            Device & Execution Logs
                        </button>
                        <button
                            className={`tab ${activeTab === 'metadata' ? 'active' : ''}`}
                            onClick={() => setActiveTab('metadata')}
                            style={{
                                flex: 1,
                                padding: '14px',
                                background: activeTab === 'metadata' ? '#1e293b' : 'transparent',
                                border: 'none',
                                color: activeTab === 'metadata' ? '#38bdf8' : '#94a3b8',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                borderBottom: activeTab === 'metadata' ? '2px solid #38bdf8' : 'none'
                            }}
                        >
                            Raw Metadata
                        </button>
                    </div>

                    <div style={{ flex: 1, padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                        {activeTab === 'screenshots' && (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {run.status === 'error' && (
                                    <div className="crash-view" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                        <div className="crash-header" style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
                                            <div className="crash-icon-wrapper" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', padding: '12px', borderRadius: '50%', color: '#ef4444' }}>
                                                <AlertCircle size={32} />
                                            </div>
                                            <div>
                                                <h3 style={{ margin: 0, color: '#f8fafc' }}>Execution Core Failure</h3>
                                                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Job aborted during setup/provisioning</span>
                                            </div>
                                        </div>
                                        <div className="crash-message-card" style={{ width: '100%', background: '#0f172a', border: '1px solid #ef4444', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                                            <pre className="crash-text" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', color: '#f87171', margin: 0, fontSize: '0.85rem' }}>
                                                {run.errorMessage || 'An error occurred before execution steps could start.'}
                                            </pre>
                                        </div>
                                    </div>
                                )}

                                {run.status !== 'error' && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                        <div className="device-frame" style={{ width: '280px', background: '#000', borderRadius: '32px', padding: '12px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)', border: '4px solid #334155' }}>
                                            <div className="device-screen" style={{ width: '100%', aspectRatio: '9/19.5', background: '#0f172a', borderRadius: '24px', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                                                {selectedStep?.screenshot ? (
                                                    <img
                                                        src={selectedStep.screenshot}
                                                        alt={`Step ${selectedStepIndex + 1}`}
                                                        style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-in' }}
                                                        onClick={() => setZoomedImage(selectedStep.screenshot)}
                                                        title="Click to Zoom"
                                                    />
                                                ) : (
                                                    <div style={{ textAlign: 'center', color: '#64748b' }}>
                                                        <Smartphone size={48} style={{ marginBottom: '8px' }} />
                                                        <p style={{ margin: 0, fontSize: '0.85rem' }}>No screenshot available</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'logs' && (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#cbd5e1' }}>Log Stream</h4>
                                    <button 
                                        className="admin-cancel-btn" 
                                        onClick={() => {
                                            navigator.clipboard.writeText(run.logs || '');
                                        }}
                                        style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                    >
                                        Copy Logs
                                    </button>
                                </div>
                                <pre 
                                    style={{
                                        flex: 1,
                                        margin: 0,
                                        padding: '16px',
                                        backgroundColor: '#0f172a',
                                        border: '1px solid #334155',
                                        borderRadius: '8px',
                                        color: '#34d399',
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        maxHeight: '400px'
                                    }}
                                >
                                    {run.logs || 'No logs recorded for this run.'}
                                </pre>
                            </div>
                        )}

                        {activeTab === 'metadata' && (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#cbd5e1' }}>Execution Details & Session Metadata</h4>
                                <pre className="metadata-pre" style={{ flex: 1, margin: 0, maxHeight: '400px' }}>
                                    {JSON.stringify({
                                        id: run.id,
                                        testGoalId: run.testGoalId,
                                        status: run.status,
                                        triggerSource: run.triggerSource,
                                        externalSessionId: run.externalSessionId,
                                        externalUrl: run.externalUrl,
                                        errorMessage: run.errorMessage,
                                        metadata: run.metadata,
                                        appConfig: run.appConfig,
                                        result: run.result,
                                        createdAt: run.createdAt,
                                        updatedAt: run.updatedAt
                                    }, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel: Step Detail & AI Analysis */}
                <div className="analysis-panel" style={{ display: 'flex', flexDirection: 'column', background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
                    <div className="panel-header" style={{ padding: '16px', borderBottom: '1px solid #334155', background: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Sparkles size={16} style={{ color: '#a855f7' }} />
                        <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Step Detail & Analysis
                        </h3>
                    </div>

                    <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
                        {/* Selected Step Inspector */}
                        {selectedStep ? (
                            <div style={{ marginBottom: '24px', borderBottom: '1px solid #334155', paddingBottom: '24px' }}>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#38bdf8', textTransform: 'uppercase' }}>Selected Step Detail</h4>
                                
                                <div style={{ display: 'grid', gap: '12px' }}>
                                    <div>
                                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block' }}>Action</span>
                                        <code style={{ fontSize: '0.85rem', color: '#cbd5e1', backgroundColor: '#0f172a', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '2px' }}>
                                            {selectedStep.action}
                                        </code>
                                    </div>

                                    {selectedStep.target && (
                                        <div>
                                            <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block' }}>Target Selector</span>
                                            <code style={{ fontSize: '0.8rem', color: '#cbd5e1', backgroundColor: '#0f172a', padding: '4px 8px', borderRadius: '4px', display: 'block', wordBreak: 'break-all', marginTop: '2px' }}>
                                                {typeof selectedStep.target === 'object' ? JSON.stringify(selectedStep.target) : selectedStep.target}
                                            </code>
                                        </div>
                                    )}

                                    {selectedStep.data && (
                                        <div>
                                            <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block' }}>Data</span>
                                            <pre style={{ margin: '2px 0 0 0', padding: '8px', backgroundColor: '#0f172a', borderRadius: '4px', fontSize: '0.8rem', color: '#cbd5e1', overflowX: 'auto' }}>
                                                {JSON.stringify(selectedStep.data, null, 2)}
                                            </pre>
                                        </div>
                                    )}

                                    {selectedStep.reasoning && (
                                        <div style={{ background: 'rgba(168, 85, 247, 0.08)', borderLeft: '3px solid #a855f7', padding: '12px', borderRadius: '4px' }}>
                                            <span style={{ fontSize: '0.75rem', color: '#c084fc', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                                                <Sparkles size={12} /> AI Reasoning
                                            </span>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#e9d5ff', lineHeight: '1.4' }}>
                                                {selectedStep.reasoning}
                                            </p>
                                        </div>
                                    )}

                                    {!selectedStep.success && selectedStep.error && (
                                        <div style={{ background: 'rgba(239, 68, 68, 0.08)', borderLeft: '3px solid #ef4444', padding: '12px', borderRadius: '4px' }}>
                                            <span style={{ fontSize: '0.75rem', color: '#f87171', display: 'block', fontWeight: 600 }}>
                                                Step Error Details
                                            </span>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#fca5a5', fontFamily: 'monospace' }}>
                                                {selectedStep.error}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginBottom: '24px' }}>
                                Select a step on the left to inspect details.
                            </div>
                        )}

                        {/* AI Summary / RCA */}
                        {run.result?.summary ? (
                            <div className="analysis-section">
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#f43f5e', textTransform: 'uppercase' }}>Root Cause Analysis</h4>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#fda4af', lineHeight: '1.5', background: 'rgba(244, 63, 94, 0.08)', borderLeft: '3px solid #f43f5e', padding: '12px', borderRadius: '4px' }}>
                                    {run.result.summary}
                                </p>
                            </div>
                        ) : run.status === 'success' ? (
                            <div style={{ textAlign: 'center', padding: '20px', background: 'rgba(16, 185, 129, 0.08)', border: '1px solid #10b981', borderRadius: '8px' }}>
                                <CheckCircle size={28} style={{ color: '#10b981', marginBottom: '8px' }} />
                                <h4 style={{ margin: '0 0 4px 0', color: '#a7f3d0' }}>Pass Metrics</h4>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#cbd5e1' }}>
                                    Steps Passed: <strong>{passCount}/{totalSteps}</strong> ({passRate}%)
                                </p>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Fullscreen Zoom Image Overlay */}
            {zoomedImage && (
                <div 
                    onClick={() => setZoomedImage(null)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.9)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2000,
                        cursor: 'zoom-out'
                    }}
                >
                    <button 
                        onClick={() => setZoomedImage(null)}
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            background: 'none',
                            border: 'none',
                            color: '#fff',
                            cursor: 'pointer'
                        }}
                    >
                        <X size={32} />
                    </button>
                    <img 
                        src={zoomedImage} 
                        alt="Zoomed Step screenshot" 
                        style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} 
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}

export default AdminRunDetail;
